import ElementUI from 'element-ui'

/* eslint-disable node/handle-callback-err */

export default class ErrorService {
  constructor (http, store, router, i18n) {
    this.$http = http
    this.$store = store
    this.$router = router
    this.$i18n = i18n
  }

  /**
   * Main error handler that processes all types of errors
   * @param {Error} error - The error object
   * @param {Object} context - Additional context (component, action, etc.)
   * @returns {Promise} - Resolves when error is handled
   */
  async handleError (error, context = {}) {
    console.error('Error occurred:', error, context)

    // Log error for debugging
    this.logError(error, context)

    // Determine error type and handle accordingly
    if (this.isNetworkError(error)) {
      return this.handleNetworkError(error, context)
    }

    if (this.isHttpError(error)) {
      return this.handleHttpError(error, context)
    }

    if (this.isValidationError(error)) {
      return this.handleValidationError(error, context)
    }

    // Handle generic errors
    return this.handleGenericError(error, context)
  }

  /**
   * Check if error is a network error
   */
  isNetworkError (error) {
    return error.message === 'Network Error' ||
           error.code === 'NETWORK_ERROR' ||
           !error.response
  }

  /**
   * Check if error is an HTTP error
   */
  isHttpError (error) {
    return error.response && error.response.status
  }

  /**
   * Check if error is a validation error
   */
  isValidationError (error) {
    return error.response && error.response.status === 422
  }

  /**
   * Handle network errors (no internet, server unreachable)
   */
  async handleNetworkError (error, context) {
    const message = this.$i18n.t('error.network.message')
    const title = this.$i18n.t('error.network.title')

    return this.showErrorDialog({
      title,
      message,
      type: 'warning',
      showRetry: true,
      retryAction: () => this.retryAction(context),
      icon: 'mdi-wifi-off'
    })
  }

  /**
   * Handle HTTP errors (4xx, 5xx)
   */
  async handleHttpError (error, context) {
    const { response } = error
    const status = response.status

    switch (status) {
      case 400:
        return this.handleBadRequest(error, context)
      case 401:
        return this.handleUnauthorized(error, context)
      case 403:
        return this.handleForbidden(error, context)
      case 404:
        return this.handleNotFound(error, context)
      case 422:
        return this.handleValidationError(error, context)
      case 429:
        return this.handleRateLimit(error, context)
      case 500:
        return this.handleServerError(error, context)
      case 502:
      case 503:
      case 504:
        return this.handleServiceUnavailable(error, context)
      default:
        return this.handleGenericHttpError(error, context)
    }
  }

  /**
   * Handle 400 Bad Request
   */
  async handleBadRequest (error, context) {
    const message = error.response.data?.message || this.$i18n.t('error.bad_request.message')
    const title = this.$i18n.t('error.bad_request.title')

    return this.showErrorNotification({
      title,
      message,
      type: 'error'
    })
  }

  /**
   * Handle 401 Unauthorized
   */
  async handleUnauthorized (error, context) {
    const message = this.$i18n.t('error.unauthorized.message')
    const title = this.$i18n.t('error.unauthorized.title')

    return this.showErrorDialog({
      title,
      message,
      type: 'warning',
      showLogin: true,
      icon: 'mdi-login'
    }).then(() => {
      this.$store.dispatch('Auth/logout')
      this.$router.push({ name: 'login' })
    })
  }

  /**
   * Handle 403 Forbidden
   */
  async handleForbidden (error, context) {
    const message = error.response.data?.message || this.$i18n.t('error.forbidden.message')
    const title = this.$i18n.t('error.forbidden.title')

    return this.showErrorDialog({
      title,
      message,
      type: 'warning',
      icon: 'mdi-shield-off'
    })
  }

  /**
   * Handle 404 Not Found
   */
  async handleNotFound (error, context) {
    const message = this.$i18n.t('error.not_found.message')
    const title = this.$i18n.t('error.not_found.title')

    return this.showErrorNotification({
      title,
      message,
      type: 'warning'
    })
  }

  /**
   * Handle 422 Validation Error
   */
  async handleValidationError (error, context) {
    const validationErrors = error.response.data

    // Show field-specific validation errors
    if (typeof validationErrors === 'object') {
      Object.keys(validationErrors).forEach(field => {
        try {
          const messages = Array.isArray(validationErrors[field])
            ? validationErrors[field]
            : [validationErrors[field]]

          messages.forEach(message => {
            try {
              this.showErrorNotification({
                title: this.$i18n.t('error.validation.title'),
                message: `${this.formatFieldName(field)}: ${message}`,
                type: 'error',
                duration: 5000
              })
            } catch (error) {
              console.error('Error showing validation notification:', error)
            }
          })
        } catch (error) {
          console.error('Error processing validation field:', error)
        }
      })
    }

    // Also show a general validation error
    return this.showErrorNotification({
      title: this.$i18n.t('error.validation.title'),
      message: this.$i18n.t('error.validation.message'),
      type: 'error'
    })
  }

  /**
   * Handle 429 Rate Limit
   */
  async handleRateLimit (error, context) {
    const retryAfter = error.response.headers['retry-after'] || 60
    const message = this.$i18n.t('error.rate_limit.message', { seconds: retryAfter })
    const title = this.$i18n.t('error.rate_limit.title')

    return this.showErrorDialog({
      title,
      message,
      type: 'warning',
      icon: 'mdi-clock-outline'
    })
  }

  /**
   * Handle 500 Server Error
   */
  async handleServerError (error, context) {
    const message = error.response.data?.message || this.$i18n.t('error.server.message')
    const title = this.$i18n.t('error.server.title')

    // Check for database connection errors
    if (message.includes('SQLSTATE[HY000]') || message.includes('SQLSTATE[2002]')) {
      return this.handleDatabaseError(error, context)
    }

    return this.showErrorDialog({
      title,
      message,
      type: 'error',
      showRetry: true,
      retryAction: () => this.retryAction(context),
      icon: 'mdi-server-off'
    })
  }

  /**
   * Handle database connection errors
   */
  async handleDatabaseError (error, context) {
    const message = this.$i18n.t('error.database.message')
    const title = this.$i18n.t('error.database.title')

    return this.showErrorDialog({
      title,
      message,
      type: 'warning',
      showRetry: true,
      retryAction: () => window.location.reload(),
      icon: 'mdi-database-off'
    })
  }

  /**
   * Handle 502, 503, 504 Service Unavailable
   */
  async handleServiceUnavailable (error, context) {
    const message = this.$i18n.t('error.service_unavailable.message')
    const title = this.$i18n.t('error.service_unavailable.title')

    return this.showErrorDialog({
      title,
      message,
      type: 'warning',
      showRetry: true,
      retryAction: () => this.retryAction(context),
      icon: 'mdi-server-network-off'
    })
  }

  /**
   * Handle generic HTTP errors
   */
  async handleGenericHttpError (error, context) {
    const message = error.response.data?.message || this.$i18n.t('error.generic.message')
    const title = this.$i18n.t('error.generic.title')

    return this.showErrorNotification({
      title,
      message,
      type: 'error'
    })
  }

  /**
   * Handle generic errors (non-HTTP)
   */
  async handleGenericError (error, context) {
    const message = error.message || this.$i18n.t('error.generic.message')
    const title = this.$i18n.t('error.generic.title')

    return this.showErrorNotification({
      title,
      message,
      type: 'error'
    })
  }

  /**
   * Show error notification (toast)
   */
  showErrorNotification ({ title, message, type = 'error', duration = 4000 }) {
    return new Promise((resolve) => {
      ElementUI.Notification({
        title,
        message,
        type,
        duration,
        showClose: true,
        dangerouslyUseHTMLString: true,
        customClass: `error-notification error-notification--${type}`
      })
      resolve()
    })
  }

  /**
   * Show error dialog (modal)
   */
  showErrorDialog ({
    title,
    message,
    type = 'error',
    showRetry = false,
    showLogin = false,
    retryAction = null,
    icon = null
  }) {
    return new Promise((resolve) => {
      // Create message content with icon and text
      let messageContent = message
      if (icon) {
        messageContent = `<i class="${icon}" style="font-size: 48px; color: ${type === 'error' ? '#f56c6c' : '#e6a23c'}; margin-bottom: 16px; display: block;"></i><div style="font-size: 16px; line-height: 1.5; color: #606266; margin-top: 16px;">${message}</div>`
      }

      ElementUI.MessageBox.confirm(messageContent, title, {
        confirmButtonText: showRetry ? this.$i18n.t('button.retry') : this.$i18n.t('button.close'),
        cancelButtonText: showLogin ? this.$i18n.t('button.login') : this.$i18n.t('button.cancel'),
        showCancelButton: showRetry || showLogin,
        type,
        customClass: `error-dialog error-dialog--${type}`,
        dangerouslyUseHTMLString: true,
        closeOnPressEscape: false,
        closeOnClickModal: false,
        confirmButtonClass: `el-button--${type === 'error' ? 'danger' : 'warning'}`,
        cancelButtonClass: 'el-button--default'
      }).then(() => {
        try {
          if (retryAction) {
            retryAction()
          }
        } catch (error) {
          console.error('Error in retry action:', error)
        }
        resolve()
      }).catch(() => {
        try {
          if (showLogin) {
            this.$store.dispatch('Auth/logout')
            this.$router.push({ name: 'login' })
          }
        } catch (error) {
          console.error('Error in login redirect:', error)
        }
        resolve()
      })
    })
  }

  /**
   * Retry the last action
   */
  retryAction (context) {
    try {
      if (context.retryFunction) {
        context.retryFunction()
      } else {
        window.location.reload()
      }
    } catch (error) {
      console.error('Error in retry action:', error)
    }
  }

  /**
   * Format field names for better display
   */
  formatFieldName (field) {
    return field
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  /**
   * Log error for debugging
   */
  logError (error, context) {
    const errorLog = {
      timestamp: new Date().toISOString(),
      error: {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      },
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    }

    console.error('Error Log:', errorLog)

    // In production, you might want to send this to a logging service
    // this.sendErrorToLoggingService(errorLog)
  }

  /**
   * Send error to logging service (for production)
   */
  sendErrorToLoggingService (errorLog) {
    // Implementation for sending errors to external logging service
    // Example: Sentry, LogRocket, etc.
  }
}
